import React, { useCallback, useEffect, useMemo, useRef, useState, memo } from 'react';
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  LinearProgress,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Pagination,
  Paper,
  Skeleton,
  Stack,
  Typography,
  Avatar,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import ChevronLeft from '@mui/icons-material/ChevronLeft';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Refresh from '@mui/icons-material/Refresh';
import Send from '@mui/icons-material/Send';
import RestaurantMenu from '@mui/icons-material/RestaurantMenu';
import Today from '@mui/icons-material/Today';
import EventAvailable from '@mui/icons-material/EventAvailable';
import moment from 'moment';
import DynamicPricingTabHeader from './DynamicPricingTabHeader';
import { getHotelTodayDateString } from '../../utils/dateUtils';
import {
  DYNAMIC_PRICING_ACCENT,
  getDynamicPricingListPaperSx,
  getDynamicPricingOutlinedButtonSx,
  getDynamicPricingPrimaryButtonSx,
  getDynamicPricingSidebarCardSx,
  getDynamicPricingThemeColors,
} from '../../utils/dynamicPricingTheme';

const RECS_PAGE_SIZE = 10;
const REC_WINDOW_DAYS = 7;

function getRangeEnd(startStr, windowDays = REC_WINDOW_DAYS) {
  return moment(startStr, 'YYYY-MM-DD').add(windowDays - 1, 'days').format('YYYY-MM-DD');
}

function countRoomTypesInRange(recs, start, end) {
  const ids = new Set();
  recs.forEach((r) => {
    const d = normalizeRecDate(r);
    if (d && d >= start && d <= end) ids.add(String(r.room_type_id));
  });
  return ids.size;
}

function buildRangeDates(start, end) {
  const dates = [];
  let cur = moment(start, 'YYYY-MM-DD');
  const last = moment(end, 'YYYY-MM-DD');
  while (cur.isSameOrBefore(last, 'day')) {
    dates.push(cur.format('YYYY-MM-DD'));
    cur.add(1, 'day');
  }
  return dates;
}

function groupByRoomType(recs, roomTypeMap, ratePlanMap) {
  const map = new Map();
  recs.forEach((rec) => {
    const rtId = rec.room_type_id;
    if (!map.has(rtId)) map.set(rtId, { room_type_id: rtId, plans: new Map() });
    const rpId = rec.rate_plan_id;
    const group = map.get(rtId);
    if (!group.plans.has(rpId)) group.plans.set(rpId, { rate_plan_id: rpId, byDate: {} });
    const date = normalizeRecDate(rec);
    if (date) group.plans.get(rpId).byDate[date] = rec;
  });
  return Array.from(map.values())
    .map((g) => ({
      room_type_id: g.room_type_id,
      plans: Array.from(g.plans.values()).sort((a, b) => {
        const na = ratePlanMap[a.rate_plan_id]?.name || ratePlanMap[a.rate_plan_id]?.code || '';
        const nb = ratePlanMap[b.rate_plan_id]?.name || ratePlanMap[b.rate_plan_id]?.code || '';
        return na.localeCompare(nb);
      }),
    }))
    .sort((a, b) => {
      const na = roomTypeMap[a.room_type_id]?.name || '';
      const nb = roomTypeMap[b.room_type_id]?.name || '';
      return na.localeCompare(nb);
    });
}

function buildRangeWindows(filteredRecs, todayStr) {
  const today = moment(todayStr, 'YYYY-MM-DD');
  let lastDate = today.clone();
  filteredRecs.forEach((r) => {
    const d = normalizeRecDate(r);
    if (d) {
      const m = moment(d, 'YYYY-MM-DD');
      if (m.isAfter(lastDate)) lastDate = m;
    }
  });

  const windows = [];
  let cursor = today.clone();
  while (cursor.isSameOrBefore(lastDate, 'day')) {
    const start = cursor.format('YYYY-MM-DD');
    const end = getRangeEnd(start);
    windows.push({
      start,
      end,
      count: countRoomTypesInRange(filteredRecs, start, end),
    });
    cursor.add(1, 'day');
  }

  if (!windows.length) {
    windows.push({ start: todayStr, end: getRangeEnd(todayStr), count: 0 });
  }
  return windows;
}

function formatRangeChipLabel(start, end, todayStr) {
  const s = moment(start, 'YYYY-MM-DD');
  const e = moment(end, 'YYYY-MM-DD');
  const rangeText = s.month() === e.month() && s.year() === e.year()
    ? `${s.format('D')}–${e.format('D MMM')}`
    : `${s.format('D MMM')}–${e.format('D MMM')}`;
  if (start === todayStr) return `Today · ${rangeText}`;
  return rangeText;
}

function normalizeRecDate(rec) {
  if (!rec?.rate_date) return null;
  return typeof rec.rate_date === 'string'
    ? rec.rate_date.slice(0, 10)
    : moment(rec.rate_date).format('YYYY-MM-DD');
}

function formatCurrency(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  return `₹${n.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

function formatDelta(base, recommended) {
  const b = Number(base);
  const r = Number(recommended);
  if (Number.isNaN(b) || Number.isNaN(r) || b === 0) return null;
  const pct = ((r - b) / b) * 100;
  return pct;
}


function isFutureOrToday(dateStr, todayStr) {
  return Boolean(dateStr && dateStr >= todayStr);
}

function RateCell({ rec, accentColor, themeColors, isDark, onApply, loading, isApplying }) {
  if (!rec) {
    return (
      <Typography variant="caption" sx={{ color: themeColors.subtleText, opacity: 0.5 }}>
        —
      </Typography>
    );
  }
  const delta = formatDelta(rec.base_rate, rec.recommended_rate);
  const isUp = delta != null && delta > 0;
  const isDown = delta != null && delta < 0;
  return (
    <Box sx={{ minWidth: 88 }}>
      <Typography variant="caption" sx={{ color: themeColors.subtleText, display: 'block', lineHeight: 1.2 }}>
        {formatCurrency(rec.base_rate)}
      </Typography>
      <Typography variant="body2" fontWeight={800} sx={{ color: accentColor, lineHeight: 1.3 }}>
        {formatCurrency(rec.recommended_rate)}
      </Typography>
      {delta != null ? (
        <Chip
          size="small"
          label={`${delta > 0 ? '+' : ''}${delta.toFixed(1)}%`}
          sx={{
            mt: 0.5,
            height: 20,
            fontSize: '0.65rem',
            fontWeight: 700,
            bgcolor: alpha(isUp ? accentColor : isDown ? '#ff8a65' : themeColors.chipBg, isDark ? 0.16 : 0.1),
            color: isUp ? accentColor : isDown ? '#ff8a65' : themeColors.subtleText,
          }}
        />
      ) : null}
      <Button
        size="small"
        variant="text"
        disabled={loading || isApplying}
        onClick={() => onApply(rec)}
        sx={{
          mt: 0.5,
          minWidth: 0,
          px: 0.5,
          py: 0,
          fontSize: '0.68rem',
          fontWeight: 700,
          color: accentColor,
          textTransform: 'none',
        }}
      >
        {isApplying ? '…' : 'Apply'}
      </Button>
    </Box>
  );
}

const RoomTypeRecommendationCard = memo(function RoomTypeRecommendationCard({
  group,
  rangeDates,
  roomTypeMap,
  ratePlanMap,
  themeColors,
  isDark,
  accentColor,
  channelLabel,
  loading,
  applyingId,
  onApplySingle,
  onApplyPlanBatch,
  recRowKey,
  todayStr,
}) {
  const rt = roomTypeMap[group.room_type_id];
  const rtName = rt?.name || rt?.code || `Room type ${group.room_type_id}`;
  const planCount = group.plans.length;

  return (
    <Card
      elevation={0}
      sx={{
        ...getDynamicPricingSidebarCardSx(isDark, accentColor),
        mb: 2,
        overflow: 'hidden',
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start" sx={{ mb: 2 }}>
          <Avatar
            src={rt?.image_url}
            sx={{
              width: 48,
              height: 48,
              bgcolor: alpha(accentColor, isDark ? 0.2 : 0.15),
              color: accentColor,
              fontWeight: 700,
              border: `1px solid ${alpha(accentColor, 0.35)}`,
            }}
          >
            {(rtName || '?').charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: themeColors.text, letterSpacing: -0.2 }}>
              {rtName}
            </Typography>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
              <Chip
                size="small"
                icon={<RestaurantMenu sx={{ fontSize: 14 }} />}
                label={`${planCount} meal plan${planCount === 1 ? '' : 's'}`}
                sx={{ height: 24, fontWeight: 600 }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={channelLabel}
                sx={{ height: 24, fontWeight: 600, borderColor: alpha(accentColor, 0.4), color: accentColor }}
              />
            </Stack>
          </Box>
        </Stack>

        <TableContainer sx={{ borderRadius: 1, border: `1px solid ${themeColors.surfaceBorder}`, overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 520 }}>
            <TableHead>
              <TableRow sx={{ bgcolor: alpha(accentColor, isDark ? 0.08 : 0.06) }}>
                <TableCell
                  sx={{
                    fontWeight: 700,
                    color: themeColors.text,
                    minWidth: 120,
                    position: 'sticky',
                    left: 0,
                    zIndex: 2,
                    bgcolor: isDark ? alpha('#0f1419', 0.95) : alpha('#fff', 0.98),
                    borderRight: `1px solid ${themeColors.surfaceBorder}`,
                  }}
                >
                  Meal plan
                </TableCell>
                {rangeDates.map((dateStr) => {
                  const isToday = dateStr === todayStr;
                  return (
                    <TableCell
                      key={dateStr}
                      align="center"
                      sx={{
                        fontWeight: 700,
                        color: isToday ? accentColor : themeColors.subtleText,
                        minWidth: 96,
                        whiteSpace: 'nowrap',
                        fontSize: '0.72rem',
                      }}
                    >
                      {isToday ? 'Today' : moment(dateStr, 'YYYY-MM-DD').format('ddd D')}
                    </TableCell>
                  );
                })}
                <TableCell
                  align="center"
                  sx={{
                    fontWeight: 700,
                    color: themeColors.text,
                    minWidth: 88,
                    position: 'sticky',
                    right: 0,
                    zIndex: 2,
                    bgcolor: isDark ? alpha('#0f1419', 0.95) : alpha('#fff', 0.98),
                    borderLeft: `1px solid ${themeColors.surfaceBorder}`,
                  }}
                >
                  Week
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {group.plans.map((plan) => {
                const planName = ratePlanMap[plan.rate_plan_id]?.name
                  || ratePlanMap[plan.rate_plan_id]?.code
                  || `Plan ${plan.rate_plan_id}`;
                const planRecs = rangeDates.map((d) => plan.byDate[d]).filter(Boolean);
                const batchKey = `batch-${group.room_type_id}-${plan.rate_plan_id}`;
                const isBatchApplying = applyingId === batchKey;
                return (
                  <TableRow
                    key={plan.rate_plan_id}
                    sx={{
                      '&:last-child td': { borderBottom: 0 },
                      '&:hover': { bgcolor: alpha(accentColor, isDark ? 0.04 : 0.03) },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        color: themeColors.text,
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: isDark ? alpha('#0f1419', 0.92) : alpha('#fff', 0.96),
                        borderRight: `1px solid ${themeColors.surfaceBorder}`,
                      }}
                    >
                      <Typography variant="body2" fontWeight={700}>
                        {planName}
                      </Typography>
                      <Typography variant="caption" sx={{ color: themeColors.subtleText }}>
                        {planRecs.length} day{planRecs.length === 1 ? '' : 's'}
                      </Typography>
                    </TableCell>
                    {rangeDates.map((dateStr) => {
                      const rec = plan.byDate[dateStr];
                      const cellKey = rec ? recRowKey(rec) : null;
                      return (
                        <TableCell key={dateStr} align="center" sx={{ verticalAlign: 'top', py: 1.25 }}>
                          <RateCell
                            rec={rec}
                            accentColor={accentColor}
                            themeColors={themeColors}
                            isDark={isDark}
                            loading={loading}
                            isApplying={cellKey && applyingId === cellKey}
                            onApply={onApplySingle}
                          />
                        </TableCell>
                      );
                    })}
                    <TableCell
                      align="center"
                      sx={{
                        verticalAlign: 'middle',
                        position: 'sticky',
                        right: 0,
                        zIndex: 1,
                        bgcolor: isDark ? alpha('#0f1419', 0.92) : alpha('#fff', 0.96),
                        borderLeft: `1px solid ${themeColors.surfaceBorder}`,
                      }}
                    >
                      <Button
                        variant="contained"
                        size="small"
                        disabled={loading || !planRecs.length || isBatchApplying}
                        onClick={() => onApplyPlanBatch(group, plan, planRecs, planName)}
                        startIcon={isBatchApplying ? <CircularProgress size={14} color="inherit" /> : <Send sx={{ fontSize: 14 }} />}
                        sx={{
                          ...getDynamicPricingPrimaryButtonSx(accentColor),
                          textTransform: 'none',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          px: 1.25,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {isBatchApplying ? 'Applying…' : 'Apply all'}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>
    </Card>
  );
});

export default function DynamicPricingRecommendationsTab({
  isDark,
  accentColor = DYNAMIC_PRICING_ACCENT,
  themeColors: themeColorsProp,
  loading,
  recommendationsFetchError,
  recommendations,
  calendarMonth,
  onCalendarMonthChange,
  channels,
  selectedChannelId,
  onSelectedChannelIdChange,
  allRoomTypesSelected,
  onAllRoomTypesSelectedChange,
  selectedRoomTypeIds,
  onSelectedRoomTypeIdsChange,
  roomTypes,
  roomTypeMap,
  ratePlanMap = {},
  onRefresh,
  onApplyRates,
}) {
  const themeColors = themeColorsProp || getDynamicPricingThemeColors(isDark);
  const todayStr = getHotelTodayDateString();
  const [selectedRangeStart, setSelectedRangeStart] = useState(todayStr);
  const selectedRangeEnd = useMemo(() => getRangeEnd(selectedRangeStart), [selectedRangeStart]);
  const [recsPage, setRecsPage] = useState(1);
  const [applyTarget, setApplyTarget] = useState(null);
  const [applyingId, setApplyingId] = useState(null);
  const dateStripRef = useRef(null);

  const filteredByRoomType = useMemo(() => {
    const recs = Array.isArray(recommendations) ? recommendations : [];
    const currentAndFuture = recs.filter((r) => isFutureOrToday(normalizeRecDate(r), todayStr));
    if (allRoomTypesSelected) return currentAndFuture;
    if (!selectedRoomTypeIds.length) return [];
    const selectedIds = new Set(selectedRoomTypeIds.map((id) => String(id)));
    return currentAndFuture.filter((r) => selectedIds.has(String(r.room_type_id)));
  }, [recommendations, allRoomTypesSelected, selectedRoomTypeIds, todayStr]);

  const datesWithRecs = useMemo(() => {
    const counts = {};
    filteredByRoomType.forEach((r) => {
      const d = normalizeRecDate(r);
      if (d && d >= todayStr) counts[d] = (counts[d] || 0) + 1;
    });
    return Object.entries(counts)
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [filteredByRoomType, todayStr]);

  const rangeWindows = useMemo(
    () => buildRangeWindows(filteredByRoomType, todayStr),
    [filteredByRoomType, todayStr],
  );

  const visibleRecs = useMemo(() => {
    return filteredByRoomType
      .filter((r) => {
        const d = normalizeRecDate(r);
        return d && d >= selectedRangeStart && d <= selectedRangeEnd;
      })
      .sort((a, b) => {
        const dateA = normalizeRecDate(a);
        const dateB = normalizeRecDate(b);
        if (dateA !== dateB) return dateA.localeCompare(dateB);
        const nameA = roomTypeMap[a.room_type_id]?.name || '';
        const nameB = roomTypeMap[b.room_type_id]?.name || '';
        return nameA.localeCompare(nameB);
      });
  }, [filteredByRoomType, selectedRangeStart, selectedRangeEnd, roomTypeMap]);

  const rangeDates = useMemo(
    () => buildRangeDates(selectedRangeStart, selectedRangeEnd),
    [selectedRangeStart, selectedRangeEnd],
  );

  const groupedRoomTypes = useMemo(
    () => groupByRoomType(visibleRecs, roomTypeMap, ratePlanMap),
    [visibleRecs, roomTypeMap, ratePlanMap],
  );

  const totalRecPages = Math.max(1, Math.ceil(groupedRoomTypes.length / RECS_PAGE_SIZE));

  const paginatedGroups = useMemo(() => {
    const start = (recsPage - 1) * RECS_PAGE_SIZE;
    return groupedRoomTypes.slice(start, start + RECS_PAGE_SIZE);
  }, [groupedRoomTypes, recsPage]);

  const recsRangeStart = groupedRoomTypes.length ? (recsPage - 1) * RECS_PAGE_SIZE + 1 : 0;
  const recsRangeEnd = groupedRoomTypes.length
    ? Math.min(recsPage * RECS_PAGE_SIZE, groupedRoomTypes.length)
    : 0;

  useEffect(() => {
    setRecsPage(1);
  }, [selectedRangeStart, selectedChannelId, allRoomTypesSelected, selectedRoomTypeIds]);

  useEffect(() => {
    if (recsPage > totalRecPages) {
      setRecsPage(totalRecPages);
    }
  }, [recsPage, totalRecPages]);

  useEffect(() => {
    if (
      selectedRangeStart >= todayStr
      && rangeWindows.some((w) => w.start === selectedRangeStart && w.count > 0)
    ) {
      return;
    }
    const firstWithRecs = rangeWindows.find((w) => w.count > 0);
    if (firstWithRecs) {
      setSelectedRangeStart(firstWithRecs.start);
      return;
    }
    if (selectedRangeStart < todayStr) {
      setSelectedRangeStart(todayStr);
    }
  }, [calendarMonth, rangeWindows, selectedRangeStart, todayStr]);

  const channelLabel = useMemo(() => {
    if (selectedChannelId === 'pms' || !selectedChannelId) return 'PMS';
    const ch = channels.find((c) => String(c.id) === String(selectedChannelId));
    return ch?.name || ch?.channel_type_display || 'Channel';
  }, [selectedChannelId, channels]);

  const selectedDateLabel = useMemo(() => {
    const s = moment(selectedRangeStart, 'YYYY-MM-DD');
    const e = moment(selectedRangeEnd, 'YYYY-MM-DD');
    if (s.year() === e.year() && s.month() === e.month()) {
      return `${s.format('dddd, MMM D')} – ${e.format('dddd, MMM D, YYYY')}`;
    }
    return `${s.format('ddd, MMM D, YYYY')} – ${e.format('ddd, MMM D, YYYY')}`;
  }, [selectedRangeStart, selectedRangeEnd]);

  const recRowKey = (rec) => `${rec.room_type_id}-${rec.rate_plan_id}-${normalizeRecDate(rec)}`;

  const handleApplyConfirm = useCallback(async () => {
    if (!applyTarget) return;
    const recs = applyTarget.mode === 'batch' ? applyTarget.recs : [applyTarget.rec];
    if (!recs.length) return;
    setApplyingId(applyTarget.applyKey);
    try {
      await onApplyRates(recs);
      setApplyTarget(null);
    } finally {
      setApplyingId(null);
    }
  }, [applyTarget, onApplyRates]);

  const handleDayClick = (dayNum) => {
    if (!dayNum) return;
    const dateStr = calendarMonth.clone().date(dayNum).format('YYYY-MM-DD');
    if (dateStr < todayStr) return;
    setSelectedRangeStart(dateStr);
  };

  const handleApplySingle = useCallback((rec) => {
    setApplyTarget({
      mode: 'single',
      rec,
      applyKey: recRowKey(rec),
    });
  }, []);

  const handleApplyPlanBatch = useCallback((group, plan, planRecs, planName) => {
    const rtName = roomTypeMap[group.room_type_id]?.name || `Type ${group.room_type_id}`;
    setApplyTarget({
      mode: 'batch',
      recs: planRecs,
      applyKey: `batch-${group.room_type_id}-${plan.rate_plan_id}`,
      title: `${rtName} · ${planName}`,
      subtitle: `${planRecs.length} rate${planRecs.length === 1 ? '' : 's'} in selected week`,
    });
  }, [roomTypeMap]);

  const showInitialSkeleton = loading && groupedRoomTypes.length === 0 && !recommendationsFetchError;
  const showRefreshBar = loading && groupedRoomTypes.length > 0;

  const activeWindowIndex = useMemo(
    () => rangeWindows.findIndex((w) => w.start === selectedRangeStart),
    [rangeWindows, selectedRangeStart],
  );

  const goToAdjacentWindow = useCallback((delta) => {
    const idx = rangeWindows.findIndex((w) => w.start === selectedRangeStart);
    const next = rangeWindows[idx + delta];
    if (next) setSelectedRangeStart(next.start);
  }, [rangeWindows, selectedRangeStart]);

  useEffect(() => {
    const strip = dateStripRef.current;
    if (!strip) return;
    const activeChip = strip.querySelector('[data-active-range="true"]');
    activeChip?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
  }, [selectedRangeStart, rangeWindows.length]);

  const renderDateStrip = () => (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <IconButton
        size="small"
        aria-label="Previous date range"
        disabled={activeWindowIndex <= 0}
        onClick={() => goToAdjacentWindow(-1)}
        sx={{
          flexShrink: 0,
          border: `1px solid ${themeColors.surfaceBorder}`,
          bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha('#000', 0.02),
          '&:disabled': { opacity: 0.35 },
        }}
      >
        <ChevronLeft fontSize="small" />
      </IconButton>
      <Box
        ref={dateStripRef}
        sx={{
          display: 'flex',
          gap: 1,
          overflowX: 'auto',
          flex: 1,
          minWidth: 0,
          pb: 0.5,
          px: 0.25,
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
      {rangeWindows.length === 0 ? (
        <Chip
          size="small"
          icon={<Today sx={{ fontSize: 16 }} />}
          label={formatRangeChipLabel(selectedRangeStart, selectedRangeEnd, todayStr)}
          sx={{ borderColor: alpha(accentColor, 0.4), color: themeColors.subtleText }}
          variant="outlined"
        />
      ) : (
        rangeWindows.map(({ start, end, count }) => {
          const isActive = start === selectedRangeStart;
          const isTodayWindow = start === todayStr;
          return (
            <Chip
              key={start}
              clickable
              data-active-range={isActive ? 'true' : undefined}
              onClick={() => setSelectedRangeStart(start)}
              icon={isTodayWindow ? <Today sx={{ fontSize: 16 }} /> : <EventAvailable sx={{ fontSize: 16 }} />}
              label={`${formatRangeChipLabel(start, end, todayStr)} · ${count} type${count === 1 ? '' : 's'}`}
              variant={isActive ? 'filled' : 'outlined'}
              sx={{
                flexShrink: 0,
                fontWeight: isActive ? 700 : 500,
                bgcolor: isActive ? alpha(accentColor, isDark ? 0.22 : 0.18) : 'transparent',
                color: isActive ? (isDark ? accentColor : '#0d3d32') : themeColors.text,
                borderColor: isActive ? alpha(accentColor, 0.55) : themeColors.surfaceBorder,
                '&:hover': {
                  bgcolor: isActive ? alpha(accentColor, isDark ? 0.28 : 0.22) : alpha(accentColor, 0.08),
                },
              }}
            />
          );
        })
      )}
      </Box>
      <IconButton
        size="small"
        aria-label="Next date range"
        disabled={activeWindowIndex < 0 || activeWindowIndex >= rangeWindows.length - 1}
        onClick={() => goToAdjacentWindow(1)}
        sx={{
          flexShrink: 0,
          border: `1px solid ${themeColors.surfaceBorder}`,
          bgcolor: isDark ? alpha('#ffffff', 0.04) : alpha('#000', 0.02),
          '&:disabled': { opacity: 0.35 },
        }}
      >
        <ChevronRight fontSize="small" />
      </IconButton>
    </Stack>
  );

  return (
    <Box>
      <DynamicPricingTabHeader
        title="Rate recommendations"
        description="One card per room type for the selected channel. All meal plans and daily rates for the 7-day window are shown together."
        action={(
          <Button
            variant="outlined"
            size="small"
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <Refresh />}
            onClick={onRefresh}
            disabled={loading}
            sx={getDynamicPricingOutlinedButtonSx(isDark, accentColor)}
          >
            Refresh
          </Button>
        )}
      />

      <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', lg: 'row' }, alignItems: 'stretch' }}>
        <Box sx={{ width: { xs: '100%', lg: 280 }, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper elevation={0} sx={{ ...getDynamicPricingSidebarCardSx(isDark, accentColor), p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: themeColors.text }}>
              Stay date
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
              <IconButton
                size="small"
                onClick={() => onCalendarMonthChange(calendarMonth.clone().subtract(1, 'month'))}
                aria-label="Previous month"
                disabled={calendarMonth.isSameOrBefore(moment(todayStr, 'YYYY-MM-DD').startOf('month'), 'month')}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Typography variant="body2" sx={{ fontWeight: 600, color: themeColors.text }}>
                {calendarMonth.format('MMMM YYYY')}
              </Typography>
              <IconButton
                size="small"
                onClick={() => onCalendarMonthChange(calendarMonth.clone().add(1, 'month'))}
                aria-label="Next month"
              >
                <ChevronRight fontSize="small" />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                <Box
                  key={`weekday-${i}`}
                  sx={{ width: '12%', textAlign: 'center', fontSize: '0.65rem', color: themeColors.subtleText, fontWeight: 600 }}
                >
                  {d}
                </Box>
              ))}
              {(() => {
                const start = calendarMonth.clone().startOf('month');
                const pad = start.day();
                const daysInMonth = calendarMonth.daysInMonth();
                const recDatesSet = new Set(datesWithRecs.map((d) => d.date));
                const cells = [];
                for (let i = 0; i < pad; i++) cells.push(null);
                for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                return cells.slice(0, 35).map((d, i) => {
                  if (!d) {
                    return <Box key={i} sx={{ width: '12%', py: 0.25 }} />;
                  }
                  const dateStr = calendarMonth.clone().date(d).format('YYYY-MM-DD');
                  const isPast = dateStr < todayStr;
                  const isInRange = dateStr >= selectedRangeStart && dateStr <= selectedRangeEnd;
                  const isRangeStart = dateStr === selectedRangeStart;
                  const hasRecs = !isPast && recDatesSet.has(dateStr);
                  const isToday = dateStr === todayStr;
                  return (
                    <Box
                      key={i}
                      component={isPast ? 'span' : 'button'}
                      type={isPast ? undefined : 'button'}
                      onClick={isPast ? undefined : () => handleDayClick(d)}
                      sx={{
                        width: '12%',
                        textAlign: 'center',
                        fontSize: '0.72rem',
                        py: 0.45,
                        border: 'none',
                        borderRadius: 1,
                        cursor: isPast ? 'default' : 'pointer',
                        position: 'relative',
                        opacity: isPast ? 0.28 : 1,
                        fontWeight: isRangeStart ? 800 : isInRange ? 700 : isToday ? 700 : 500,
                        color: isRangeStart ? (isDark ? '#04120e' : '#0d3d32') : themeColors.text,
                        bgcolor: isRangeStart
                          ? accentColor
                          : isInRange
                            ? alpha(accentColor, isDark ? 0.18 : 0.14)
                            : hasRecs
                              ? alpha(accentColor, isDark ? 0.1 : 0.08)
                              : 'transparent',
                        '&:hover': isPast ? {} : { bgcolor: isRangeStart ? accentColor : alpha(accentColor, 0.18) },
                      }}
                    >
                      {d}
                      {hasRecs && !isInRange ? (
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 1,
                            left: '50%',
                            transform: 'translateX(-50%)',
                            width: 4,
                            height: 4,
                            borderRadius: '50%',
                            bgcolor: accentColor,
                          }}
                        />
                      ) : null}
                    </Box>
                  );
                });
              })()}
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ ...getDynamicPricingSidebarCardSx(isDark, accentColor), p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: themeColors.text }}>
              Channel
            </Typography>
            <List dense disablePadding>
              <ListItemButton
                dense
                selected={selectedChannelId === 'pms'}
                onClick={() => onSelectedChannelIdChange('pms')}
                sx={{ py: 0.25, borderRadius: 1 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Box sx={{ width: 28, height: 28, borderRadius: 1, bgcolor: alpha(accentColor, 0.15), display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700, color: accentColor }}>
                    PMS
                  </Box>
                </ListItemIcon>
                <ListItemText primary="PMS" primaryTypographyProps={{ variant: 'body2' }} />
              </ListItemButton>
              {channels.map((ch) => {
                const chName = ch.name || ch.channel_type_display || `Channel ${ch.id}`;
                return (
                  <ListItemButton
                    key={ch.id}
                    dense
                    selected={String(selectedChannelId) === String(ch.id)}
                    onClick={() => onSelectedChannelIdChange(ch.id)}
                    sx={{ py: 0.25, borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Avatar src={ch.image_path} sx={{ width: 28, height: 28, bgcolor: 'action.selected', fontSize: '0.75rem' }}>
                        {(chName || '?').charAt(0).toUpperCase()}
                      </Avatar>
                    </ListItemIcon>
                    <ListItemText primary={chName} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>

          <Paper elevation={0} sx={{ ...getDynamicPricingSidebarCardSx(isDark, accentColor), p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: themeColors.text }}>
              Room types
            </Typography>
            <FormControlLabel
              control={(
                <Checkbox
                  size="small"
                  checked={allRoomTypesSelected}
                  onChange={(e) => {
                    onAllRoomTypesSelectedChange(e.target.checked);
                    if (e.target.checked) onSelectedRoomTypeIdsChange([]);
                  }}
                />
              )}
              label={<Typography variant="body2">All room types</Typography>}
            />
            <List dense disablePadding sx={{ mt: 0.5 }}>
              {roomTypes.map((rt) => {
                const name = rt.name || rt.code || `Room type ${rt.id}`;
                const checked = allRoomTypesSelected || selectedRoomTypeIds.includes(rt.id);
                return (
                  <ListItemButton
                    key={rt.id}
                    dense
                    onClick={() => {
                      if (allRoomTypesSelected) {
                        onAllRoomTypesSelectedChange(false);
                        onSelectedRoomTypeIdsChange([rt.id]);
                      } else {
                        onSelectedRoomTypeIdsChange((prev) =>
                          prev.includes(rt.id) ? prev.filter((id) => id !== rt.id) : [...prev, rt.id],
                        );
                      }
                    }}
                    sx={{ py: 0.25, borderRadius: 1 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Checkbox size="small" checked={checked} disableRipple tabIndex={-1} />
                    </ListItemIcon>
                    <Avatar src={rt.image_url} sx={{ width: 28, height: 28, mr: 1, bgcolor: 'action.selected', fontSize: '0.75rem' }}>
                      {(name || '?').charAt(0).toUpperCase()}
                    </Avatar>
                    <ListItemText primary={name} primaryTypographyProps={{ variant: 'body2', noWrap: true }} />
                  </ListItemButton>
                );
              })}
            </List>
          </Paper>
        </Box>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Paper
            elevation={0}
            sx={{
              ...getDynamicPricingListPaperSx(isDark),
              overflow: 'visible',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ mb: 2 }}>
              <Typography variant="overline" sx={{ color: accentColor, fontWeight: 800, letterSpacing: 0.12 }}>
                Recommendations
              </Typography>
              <Typography variant="body2" sx={{ color: themeColors.subtleText, mb: 1.5, mt: 0.5 }}>
                {selectedDateLabel}
                {groupedRoomTypes.length
                  ? ` · ${groupedRoomTypes.length} room type${groupedRoomTypes.length === 1 ? '' : 's'} · ${channelLabel}`
                  : ''}
              </Typography>
              {renderDateStrip()}
            </Box>

            {showRefreshBar ? (
              <LinearProgress
                sx={{
                  mb: 1,
                  borderRadius: 1,
                  bgcolor: alpha(accentColor, 0.12),
                  '& .MuiLinearProgress-bar': { bgcolor: accentColor },
                }}
              />
            ) : null}

            {showInitialSkeleton ? (
              <Box sx={{ py: 1 }}>
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i} sx={{ display: 'flex', gap: 2, mb: 2 }}>
                    <Skeleton variant="circular" width={44} height={44} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="40%" height={24} />
                      <Skeleton width="70%" height={20} sx={{ mt: 1 }} />
                    </Box>
                    <Skeleton width={72} height={32} />
                  </Box>
                ))}
              </Box>
            ) : recommendationsFetchError ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="error" sx={{ mb: 2 }}>
                  Could not load recommendations.
                </Typography>
                <Button variant="outlined" onClick={onRefresh} sx={getDynamicPricingOutlinedButtonSx(isDark, accentColor)}>
                  Try again
                </Button>
              </Box>
            ) : groupedRoomTypes.length === 0 ? (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography sx={{ color: themeColors.subtleText, mb: 1 }}>
                  No recommendations for {selectedRangeStart === todayStr ? 'the current 7-day window' : selectedDateLabel}.
                </Typography>
                <Typography variant="body2" sx={{ color: themeColors.subtleText }}>
                  Try another date range, channel, or room type filter.
                </Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ opacity: loading ? 0.72 : 1, transition: 'opacity 0.2s ease' }}>
                  {paginatedGroups.map((group) => (
                    <RoomTypeRecommendationCard
                      key={group.room_type_id}
                      group={group}
                      rangeDates={rangeDates}
                      roomTypeMap={roomTypeMap}
                      ratePlanMap={ratePlanMap}
                      themeColors={themeColors}
                      isDark={isDark}
                      accentColor={accentColor}
                      channelLabel={channelLabel}
                      loading={loading}
                      applyingId={applyingId}
                      onApplySingle={handleApplySingle}
                      onApplyPlanBatch={handleApplyPlanBatch}
                      recRowKey={recRowKey}
                      todayStr={todayStr}
                    />
                  ))}
                </Box>
                {groupedRoomTypes.length > RECS_PAGE_SIZE ? (
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1.5}
                    alignItems={{ xs: 'stretch', sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ mt: 2, pt: 2, borderTop: `1px solid ${themeColors.surfaceBorder}` }}
                  >
                    <Typography variant="caption" sx={{ color: themeColors.subtleText }}>
                      Showing {recsRangeStart}–{recsRangeEnd} of {groupedRoomTypes.length} room types
                    </Typography>
                    <Pagination
                      count={totalRecPages}
                      page={recsPage}
                      onChange={(_, page) => setRecsPage(page)}
                      color="primary"
                      size="small"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          color: themeColors.text,
                          borderColor: themeColors.surfaceBorder,
                        },
                        '& .Mui-selected': {
                          bgcolor: `${alpha(accentColor, isDark ? 0.22 : 0.18)} !important`,
                          color: `${isDark ? accentColor : '#0d3d32'} !important`,
                          fontWeight: 700,
                        },
                      }}
                    />
                  </Stack>
                ) : null}
              </>
            )}
          </Paper>
        </Box>
      </Box>

      <Dialog open={Boolean(applyTarget)} onClose={() => !applyingId && setApplyTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {applyTarget?.mode === 'batch' ? 'Apply meal plan rates?' : 'Apply this rate?'}
        </DialogTitle>
        <DialogContent>
          {applyTarget?.mode === 'batch' ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Publish <strong>{applyTarget.subtitle}</strong> for{' '}
                <strong>{applyTarget.title}</strong> to <strong>{channelLabel}</strong> ({selectedDateLabel}).
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 200, overflow: 'auto', borderColor: 'divider' }}>
                {applyTarget.recs.slice(0, 10).map((rec) => (
                  <Typography key={recRowKey(rec)} variant="body2" sx={{ py: 0.35 }}>
                    {moment(normalizeRecDate(rec), 'YYYY-MM-DD').format('ddd, MMM D')}:{' '}
                    {formatCurrency(rec.base_rate)} →{' '}
                    <strong style={{ color: accentColor }}>{formatCurrency(rec.recommended_rate)}</strong>
                  </Typography>
                ))}
                {applyTarget.recs.length > 10 ? (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    + {applyTarget.recs.length - 10} more day{applyTarget.recs.length - 10 === 1 ? '' : 's'}
                  </Typography>
                ) : null}
              </Paper>
            </>
          ) : applyTarget?.rec ? (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Publish recommended rate for{' '}
                <strong>{roomTypeMap[applyTarget.rec.room_type_id]?.name || `Type ${applyTarget.rec.room_type_id}`}</strong>
                {' · '}
                <strong>
                  {ratePlanMap[applyTarget.rec.rate_plan_id]?.name
                    || ratePlanMap[applyTarget.rec.rate_plan_id]?.code
                    || `Plan ${applyTarget.rec.rate_plan_id}`}
                </strong>{' '}
                on <strong>{moment(normalizeRecDate(applyTarget.rec), 'YYYY-MM-DD').format('dddd, MMM D, YYYY')}</strong> to{' '}
                <strong>{channelLabel}</strong>.
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, borderColor: 'divider' }}>
                <Typography variant="body2">
                  {formatCurrency(applyTarget.rec.base_rate)} →{' '}
                  <strong style={{ color: accentColor }}>{formatCurrency(applyTarget.rec.recommended_rate)}</strong>
                </Typography>
              </Paper>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setApplyTarget(null)} disabled={Boolean(applyingId)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyConfirm}
            disabled={Boolean(applyingId) || !applyTarget}
            startIcon={applyingId ? <CircularProgress size={18} color="inherit" /> : <Send />}
            sx={getDynamicPricingPrimaryButtonSx(accentColor)}
          >
            {applyingId ? 'Applying…' : applyTarget?.mode === 'batch' ? 'Confirm apply all' : 'Confirm apply'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
